// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import React, { useState, useEffect } from 'react';
import { fetchConfig, AppConfigModel } from '../utils/AppUtils';

interface FeedbackWidgetProps {
  /** Unique identifier for the feedback (e.g., serverCallId or assessment id) */
  feedbackId: string;
  /** Type of content being rated: 'assessment' or 'learning_path' */
  feedbackType: 'assessment' | 'learning_path';
  /** Optional callback when feedback is submitted */
  onFeedbackSubmitted?: (rating: 'positive' | 'negative') => void;
  /** User email (required for assessment feedback) */
  userEmail?: string;
  /** Assessment data (required for assessment feedback) */
  assessmentData?: unknown;
  /** Server call ID to be sent in prompt_question */
  serverCallId?: string;
}

const ThumbsUpIcon = ({ filled }: { filled: boolean }): JSX.Element => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill={filled ? 'currentColor' : 'none'}
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" />
  </svg>
);

const ThumbsDownIcon = ({ filled }: { filled: boolean }): JSX.Element => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill={filled ? 'currentColor' : 'none'}
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17" />
  </svg>
);

export const FeedbackWidget: React.FC<FeedbackWidgetProps> = ({
  feedbackId,
  feedbackType,
  onFeedbackSubmitted,
  userEmail,
  assessmentData,
  serverCallId
}) => {
  const [rating, setRating] = useState<'positive' | 'negative' | null>(null);
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState<AppConfigModel | null>(null);

  useEffect(() => {
    const loadConfig = async (): Promise<void> => {
      try {
        const appConfig = await fetchConfig();
        setConfig(appConfig);
      } catch (error) {
        console.error('Error loading config:', error);
      }
    };
    loadConfig();
  }, []);

  const handleFeedback = async (selectedRating: 'positive' | 'negative'): Promise<void> => {
    // Prevent duplicate submissions for the same rating
    if (loading || !config || rating === selectedRating) return;

    setRating(selectedRating);
    setLoading(true);

    try {
      // Use external API for both assessment and learning_path feedback
      const payload = {
        user_preference: selectedRating === 'positive' ? 1 : 0,
        email_id: userEmail || '',
        agent_name: 'AI Skill Assessment and Learning Pathway Agent',
        prompt_question: serverCallId || '',
        prompt_response: assessmentData ? JSON.stringify(assessmentData) : '',
        feedback_type: feedbackType
      };

      const response = await fetch(`${config.feedbackApiEndpoint}/save_user_preferences_ext`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error('Failed to submit feedback');
      }

      onFeedbackSubmitted?.(selectedRating);
    } catch (error) {
      console.error('Error submitting feedback:', error);
      setRating(null);
    } finally {
      setLoading(false);
    }
  };

  const containerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '16px 20px',
    backgroundColor: '#f9fafb',
    borderRadius: '8px',
    marginTop: '24px',
    border: '1px solid #e5e7eb'
  };

  const labelStyle: React.CSSProperties = {
    fontSize: '14px',
    fontWeight: 500,
    color: '#374151',
    marginRight: '8px'
  };

  const buttonStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '8px 12px',
    backgroundColor: 'transparent',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    cursor: loading ? 'not-allowed' : 'pointer',
    transition: 'all 0.2s ease',
    color: '#6b7280',
    opacity: loading ? 0.5 : 1
  };

  const activeButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    backgroundColor: '#00d2aa',
    borderColor: '#00d2aa',
    color: '#ffffff',
    opacity: 1
  };

  return (
    <div style={containerStyle}>
      <span style={labelStyle}>Was this helpful?</span>
      <button
        type="button"
        style={rating === 'positive' ? activeButtonStyle : buttonStyle}
        onClick={() => handleFeedback('positive')}
        disabled={loading}
        aria-label="Thumbs up"
        onMouseEnter={(e) => {
          if (!loading && rating !== 'positive') {
            e.currentTarget.style.backgroundColor = '#f3f4f6';
            e.currentTarget.style.borderColor = '#00d2aa';
          }
        }}
        onMouseLeave={(e) => {
          if (rating !== 'positive') {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.borderColor = '#d1d5db';
          }
        }}
      >
        <ThumbsUpIcon filled={rating === 'positive'} />
      </button>
      <button
        type="button"
        style={rating === 'negative' ? activeButtonStyle : buttonStyle}
        onClick={() => handleFeedback('negative')}
        disabled={loading}
        aria-label="Thumbs down"
        onMouseEnter={(e) => {
          if (!loading && rating !== 'negative') {
            e.currentTarget.style.backgroundColor = '#f3f4f6';
            e.currentTarget.style.borderColor = '#ef4444';
          }
        }}
        onMouseLeave={(e) => {
          if (rating !== 'negative') {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.borderColor = '#d1d5db';
          }
        }}
      >
        <ThumbsDownIcon filled={rating === 'negative'} />
      </button>
    </div>
  );
};
